import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import api from '../lib/api';

const ReservationPrint = () => {
  const { code } = useParams();
  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReservation = async () => {
      try {
        const response = await api.get(`/reservations/code/${code}`);
        setReservation(response.data);
      } catch (err) {
        setError('Reserva no encontrada');
      } finally {
        setLoading(false);
      }
    };

    if (code) {
      fetchReservation();
    }
  }, [code]);

  useEffect(() => {
    // Auto-print when loaded
    if (reservation && !loading) {
      setTimeout(() => {
        window.print();
      }, 500);
    }
  }, [reservation, loading]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), "EEEE d 'de' MMMM yyyy, HH:mm", { locale: es });
    } catch {
      return dateStr;
    }
  };

  const formatDateShort = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), "dd/MM/yyyy HH:mm", { locale: es });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="print-container">
        <p>Cargando...</p>
      </div>
    );
  }

  if (error || !reservation) {
    return (
      <div className="print-container">
        <p>{error || 'Reserva no encontrada'}</p>
      </div>
    );
  }

  const paymentMethodText = {
    'CASH': 'Efectivo',
    'TPV': 'Tarjeta/Bizum',
    'STRIPE': 'Pago Online'
  };

  const statusText = {
    'PENDING_PAYMENT': 'Pendiente de pago',
    'CONFIRMED': 'Confirmada',
    'COMPLETED': 'Completada',
    'CANCELLED': 'Cancelada',
    'NO_SHOW': 'No presentado'
  };

  return (
    <>
      <style>
        {`
          @media print {
            @page {
              size: A4;
              margin: 15mm;
            }
            body {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .no-print {
              display: none !important;
            }
          }
          
          .print-container {
            font-family: 'Arial', sans-serif;
            max-width: 800px;
            margin: 0 auto;
            padding: 20px;
            background: white;
            color: #000;
          }
          
          .header {
            text-align: center;
            border-bottom: 3px solid #FFD700;
            padding-bottom: 20px;
            margin-bottom: 30px;
          }
          
          .logo {
            font-size: 28px;
            font-weight: bold;
            color: #000;
            margin-bottom: 5px;
          }
          
          .logo-sub {
            font-size: 14px;
            color: #666;
          }
          
          .reservation-code {
            background: #FFD700;
            color: #000;
            font-size: 32px;
            font-weight: bold;
            padding: 15px 30px;
            display: inline-block;
            margin: 20px 0;
            border-radius: 8px;
            font-family: monospace;
          }
          
          .section {
            margin-bottom: 25px;
            page-break-inside: avoid;
          }
          
          .section-title {
            font-size: 14px;
            font-weight: bold;
            color: #333;
            text-transform: uppercase;
            border-bottom: 2px solid #FFD700;
            padding-bottom: 8px;
            margin-bottom: 15px;
          }
          
          .info-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 15px;
          }
          
          .info-item {
            padding: 10px;
            background: #f9f9f9;
            border-radius: 5px;
          }
          
          .info-label {
            font-size: 11px;
            color: #666;
            text-transform: uppercase;
            margin-bottom: 5px;
          }
          
          .info-value {
            font-size: 16px;
            font-weight: 600;
            color: #000;
          }
          
          .info-value.large {
            font-size: 24px;
          }
          
          .info-value.mono {
            font-family: monospace;
            letter-spacing: 1px;
          }
          
          .dates-row {
            display: flex;
            justify-content: space-between;
            gap: 20px;
          }
          
          .date-box {
            flex: 1;
            background: #f5f5f5;
            padding: 15px;
            border-radius: 8px;
            text-align: center;
          }
          
          .date-box.entrada {
            border-left: 4px solid #22c55e;
          }
          
          .date-box.salida {
            border-left: 4px solid #ef4444;
          }
          
          .date-label {
            font-size: 12px;
            color: #666;
            text-transform: uppercase;
            margin-bottom: 8px;
          }
          
          .date-value {
            font-size: 14px;
            font-weight: 600;
          }
          
          .total-box {
            background: #000;
            color: #fff;
            padding: 20px;
            border-radius: 8px;
            text-align: center;
            margin-top: 20px;
          }
          
          .total-label {
            font-size: 14px;
            color: #ccc;
            margin-bottom: 5px;
          }
          
          .total-value {
            font-size: 36px;
            font-weight: bold;
            color: #FFD700;
          }
          
          .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #ddd;
            text-align: center;
            font-size: 12px;
            color: #666;
          }
          
          .qr-section {
            text-align: center;
            margin: 20px 0;
          }
          
          .qr-code {
            width: 150px;
            height: 150px;
          }
          
          .signature-area {
            margin-top: 40px;
            display: flex;
            justify-content: space-between;
          }
          
          .signature-box {
            width: 45%;
            text-align: center;
          }
          
          .signature-line {
            border-top: 1px solid #000;
            margin-top: 60px;
            padding-top: 10px;
            font-size: 12px;
          }
          
          .print-btn {
            position: fixed;
            top: 20px;
            right: 20px;
            background: #FFD700;
            color: #000;
            border: none;
            padding: 12px 24px;
            font-size: 16px;
            font-weight: bold;
            border-radius: 8px;
            cursor: pointer;
          }
          
          .print-btn:hover {
            background: #e6c200;
          }
          
          .notes-box {
            background: #fff9e6;
            border: 1px solid #FFD700;
            padding: 15px;
            border-radius: 8px;
            margin-top: 15px;
          }
        `}
      </style>

      <button className="print-btn no-print" onClick={() => window.print()}>
        Imprimir
      </button>

      <div className="print-container">
        {/* Header */}
        <div className="header">
          <div className="logo">PLEYADES PARKING</div>
          <div className="logo-sub">Servicio de Valet Parking - Aeropuerto de Madrid</div>
          <div className="reservation-code">{reservation.reservation_code}</div>
          <div style={{ fontSize: '14px', color: '#666' }}>
            Confirmación de Reserva
          </div>
        </div>

        {/* Dates Section */}
        <div className="section">
          <div className="section-title">Fechas del Servicio</div>
          <div className="dates-row">
            <div className="date-box entrada">
              <div className="date-label">Fecha de Entrada</div>
              <div className="date-value">{formatDate(reservation.start_dt)}</div>
              {reservation.arrival_terminal && (
                <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
                  Terminal: <strong>{reservation.arrival_terminal}</strong>
                </div>
              )}
              {reservation.arrival_flight && (
                <div style={{ fontSize: '13px', color: '#666' }}>
                  Vuelo: <strong>{reservation.arrival_flight}</strong>
                </div>
              )}
            </div>
            <div className="date-box salida">
              <div className="date-label">Fecha de Salida</div>
              <div className="date-value">{formatDate(reservation.end_dt)}</div>
              {reservation.departure_terminal && (
                <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
                  Terminal: <strong>{reservation.departure_terminal}</strong>
                </div>
              )}
              {reservation.departure_flight && (
                <div style={{ fontSize: '13px', color: '#666' }}>
                  Vuelo: <strong>{reservation.departure_flight}</strong>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Customer Section - NO EMAIL */}
        <div className="section">
          <div className="section-title">Datos del Cliente</div>
          <div className="info-grid">
            <div className="info-item">
              <div className="info-label">Nombre</div>
              <div className="info-value">{reservation.customer_name}</div>
            </div>
            <div className="info-item">
              <div className="info-label">Teléfono</div>
              <div className="info-value">{reservation.customer_phone || '-'}</div>
            </div>
          </div>
        </div>

        {/* Vehicle Section */}
        <div className="section">
          <div className="section-title">Datos del Vehículo</div>
          <div className="info-grid">
            <div className="info-item">
              <div className="info-label">Matrícula</div>
              <div className="info-value large mono">{reservation.vehicle_plate}</div>
            </div>
            <div className="info-item">
              <div className="info-label">Marca / Modelo</div>
              <div className="info-value">
                {reservation.vehicle_brand || '-'} {reservation.vehicle_model || ''}
              </div>
            </div>
            <div className="info-item">
              <div className="info-label">Color</div>
              <div className="info-value">{reservation.vehicle_color || '-'}</div>
            </div>
            <div className="info-item">
              <div className="info-label">Estado</div>
              <div className="info-value">{statusText[reservation.status] || reservation.status}</div>
            </div>
          </div>
        </div>

        {/* Payment Section */}
        <div className="section">
          <div className="section-title">Información de Pago</div>
          <div className="info-grid">
            <div className="info-item">
              <div className="info-label">Método de Pago</div>
              <div className="info-value">{paymentMethodText[reservation.payment_method] || reservation.payment_method}</div>
            </div>
            <div className="info-item">
              <div className="info-label">Estado del Pago</div>
              <div className="info-value">{reservation.payment_status === 'PAID' ? 'Pagado' : 'Pendiente'}</div>
            </div>
          </div>
          
          {reservation.selected_extras && reservation.selected_extras.length > 0 && (
            <div className="notes-box" style={{ marginTop: '15px' }} data-testid="print-extras-box">
              <div style={{ fontSize: '12px', fontWeight: 'bold', textTransform: 'uppercase', color: '#8a6d00', marginBottom: '8px' }}>
                ✨ Servicios Extras Solicitados
              </div>
              {reservation.selected_extras.map((extra, idx) => (
                <div key={`extra-${idx}`} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', padding: '3px 0' }}>
                  <span>• {extra.name || 'Extra'}</span>
                  <strong>{((extra.price_cents || 0) / 100).toFixed(2)} €</strong>
                </div>
              ))}
              {reservation.extras_total_cents > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', borderTop: '1px solid #FFD700', marginTop: '8px', paddingTop: '6px', color: '#666' }}>
                  <span>Subtotal extras</span>
                  <strong>{(reservation.extras_total_cents / 100).toFixed(2)} €</strong>
                </div>
              )}
            </div>
          )}
          
          <div className="total-box">
            <div className="total-label">TOTAL A PAGAR</div>
            <div className="total-value">{(reservation.total_cents / 100).toFixed(2)} €</div>
          </div>
        </div>

        {/* Notes */}
        {reservation.notes && (
          <div className="section">
            <div className="section-title">Notas</div>
            <div className="notes-box">
              {reservation.notes}
            </div>
          </div>
        )}

        {/* Signature Area */}
        <div className="signature-area">
          <div className="signature-box">
            <div className="signature-line">Firma del Cliente</div>
          </div>
          <div className="signature-box">
            <div className="signature-line">Firma Pleyades Parking</div>
          </div>
        </div>

        {/* Footer */}
        <div className="footer">
          <p><strong>Pleyades Parking</strong> - Aeropuerto Adolfo Suárez Madrid-Barajas</p>
          <p>Tel: 641 243 287 | info@pleyadesparking.es</p>
          <p style={{ marginTop: '10px', fontSize: '11px' }}>
            Documento generado el {formatDateShort(new Date().toISOString())}
          </p>
        </div>
      </div>
    </>
  );
};

export default ReservationPrint;
